import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiRequestError } from "../../lib/api";

interface MemberRow {
  userId: string;
  displayName: string;
  role: "admin" | "member";
  status: "invited" | "active" | "suspended";
}

interface InvitationRow {
  id: string;
  email: string;
  role: "admin" | "member";
  status: "pending" | "accepted" | "expired" | "revoked";
  expiresAt: string;
}

const MAX_MEMBERS = 5;

export function AdminPage() {
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [invitations, setInvitations] = useState<InvitationRow[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [error, setError] = useState<string | null>(null);
  const [lastToken, setLastToken] = useState<{ email: string; url: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reload = useCallback(() => {
    void api.get<{ members: MemberRow[] }>("/api/v1/members").then((r) => setMembers(r.members));
    void api.get<{ invitations: InvitationRow[] }>("/api/v1/invitations").then((r) => setInvitations(r.invitations));
  }, []);

  useEffect(() => reload(), [reload]);

  const pendingCount = invitations?.filter((i) => i.status === "pending").length ?? 0;
  const usedSlots = (members?.length ?? 0) + pendingCount;

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLastToken(null);
    setSubmitting(true);
    try {
      const result = await api.post<{ token: string }>("/api/v1/invitations", { email, role });
      const url = `${window.location.origin}/accept-invitation?token=${encodeURIComponent(result.token)}`;
      setLastToken({ email, url });
      setEmail("");
      reload();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Einladung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRevoke(id: string) {
    await api.post(`/api/v1/invitations/${id}/revoke`);
    reload();
  }

  async function onSuspend(userId: string) {
    try {
      await api.post(`/api/v1/members/${userId}/suspend`);
      reload();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Aktion fehlgeschlagen.");
    }
  }

  async function onReactivate(userId: string) {
    await api.post(`/api/v1/members/${userId}/reactivate`);
    reload();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.3rem", fontSize: "1.3rem", fontWeight: 600 }}>Administration</h1>
        <p style={{ margin: "0 0 0.6rem", fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>
          Plätze belegt: {usedSlots} / {MAX_MEMBERS}
        </p>
        <Link to="/admin/collections" className="pw-button">
          Sammlungen verwalten
        </Link>{" "}
        <Link to="/admin/system" className="pw-button">
          System / Verarbeitung
        </Link>{" "}
        <Link to="/admin/podcast" className="pw-button">
          Podcast verwalten
        </Link>
      </section>

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Einladen</h2>
        {error && <div className="pw-alert pw-alert--error">{error}</div>}
        {lastToken && (
          <div className="pw-alert pw-alert--info">
            Link für {lastToken.email} (wird nur jetzt angezeigt):
            <br />
            <code style={{ wordBreak: "break-all", fontFamily: "var(--pw-font-mono)" }}>{lastToken.url}</code>
          </div>
        )}
        <form onSubmit={(e) => void onInvite(e)} noValidate style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="pw-field" style={{ flex: 1, minWidth: "220px", marginBottom: 0 }}>
            <label htmlFor="invite-email">E-Mail</label>
            <input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="pw-field" style={{ marginBottom: 0 }}>
            <label htmlFor="invite-role">Rolle</label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
              style={{ padding: "0.65rem 0.8rem", borderRadius: "var(--pw-radius-sm)", background: "var(--pw-color-surface)", border: "1px solid var(--pw-color-surface-border)", color: "var(--pw-color-text)" }}
            >
              <option value="member">Mitglied</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          <button type="submit" className="pw-button pw-button--primary" disabled={submitting || usedSlots >= MAX_MEMBERS}>
            {usedSlots >= MAX_MEMBERS ? "Limit erreicht" : submitting ? "Wird gesendet …" : "Einladen"}
          </button>
        </form>
      </section>

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Offene Einladungen</h2>
        {invitations?.filter((i) => i.status === "pending").length === 0 && (
          <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--pw-color-text-muted)" }}>Keine offenen Einladungen.</p>
        )}
        {invitations
          ?.filter((i) => i.status === "pending")
          .map((inv) => (
            <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0", borderBottom: "1px solid var(--pw-color-surface-flat-border)" }}>
              <span style={{ flex: 1 }}>{inv.email}</span>
              <span style={{ fontSize: "0.75rem", color: "var(--pw-color-text-muted)" }}>{inv.role}</span>
              <button type="button" className="pw-button" onClick={() => void onRevoke(inv.id)}>
                Widerrufen
              </button>
            </div>
          ))}
      </section>

      <section className="pw-card">
        <h2 style={{ margin: "0 0 0.8rem", fontSize: "1.05rem", fontWeight: 600 }}>Mitglieder</h2>
        {members?.map((m) => (
          <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0", borderBottom: "1px solid var(--pw-color-surface-flat-border)" }}>
            <span style={{ flex: 1 }}>{m.displayName}</span>
            <span style={{ fontSize: "0.75rem", color: "var(--pw-color-text-muted)" }}>
              {m.role} · {m.status}
            </span>
            {m.status === "active" ? (
              <button type="button" className="pw-button" onClick={() => void onSuspend(m.userId)}>
                Sperren
              </button>
            ) : m.status === "suspended" ? (
              <button type="button" className="pw-button" onClick={() => void onReactivate(m.userId)}>
                Entsperren
              </button>
            ) : null}
          </div>
        ))}
      </section>
    </div>
  );
}
